import device from '@play-co/timestep-core/lib/device';
import animate from '@play-co/timestep-core/lib/animate';
import View, { ViewPosition } from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import uiConfig from './ui/config';
import { hideLoading, showLoading } from 'src/state/ui';
import AssetGroup from '@play-co/timestep-core/lib/ui/resource/AssetGroup';
import * as beautifyNumber from 'src/replicant/utils/beautifyNumber';
import supportEmail from 'src/replicant/ruleset/supportEmail';
import { createImageDescriptor, engine } from '@play-co/timestep-core';
import platform, {
  Platform,
  PlatformLineGuest,
  Product,
  analytics,
} from '@play-co/gcinstant';
import Application from '../Application';
import { ScreenId } from '../game/logic/ScreenManager';
import { assertNever } from 'src/replicant/utils';
import {
  ImageView,
  ImageViewOpts,
  MultiplyFilter,
  QueryablePromise,
  QuickViewPool,
  createQueryablePromiseFromPromise,
  getRelativeOffset,
} from '@play-co/timestep-core/ui';
import { prices, webIAPPrices } from '../replicant/iap/iap.ruleset';
import { shopBundlesGetWebIAPCatalog } from 'src/game/minigame/match2-timestep/shopbundles/match2-shopbundles.utils';
import { match2BundleSaleGetWebIAPCatalog } from 'src/game/minigame/match2-timestep/bundlesale/match2-bundlesale.utils';
import { duration } from 'src/replicant/utils/duration';
import { FeatureAnalytics } from 'src/replicant/analytics';
import { isPlatformNative } from './native/native.util';

export type System = {
  start: () => void;
  stop: () => void;
};

// screen dimensions and scales

export const getApplicationScale = () => {
  return (device.height / uiConfig.height) * getExtraScale();
};

export const getExtraScale = () => {
  // calculate extra scaling for large devices
  // extraScale is based on different screen ratios,
  // starting from what we consider a `normal` one, which in this case is GalaxyS5 = 9:16 = 0.5625

  // also, extraScaling will never be over 1, which means that assets
  // won't scale up from their original size

  // Update: We will allow assets to scale up to maximum allowed scale,
  // mainly to compensate for fb top bar

  const maxAllowedScale = 1; //uiConfig.height / 1080;
  const screenRatio = device.width / device.height;
  const normalScreenRatio = 0.5625; // GalaxyS5 (9: 16)
  const extraScale = Math.min(
    (screenRatio * 1) / normalScreenRatio,
    maxAllowedScale,
  );

  return extraScale;
};

// from 3:4 to 9:16 = 0.5625
// from 9:16 to 3:4 = 0.8
export const mapScaleUpFactor = uiConfig.height / 1024;

export const getScreenDimensions = () => {
  const scale = getApplicationScale();
  const w = Math.ceil(device.width / scale);
  let h = Math.ceil(device.height / scale);
  return { width: w, height: h };
};

export const getScreenExtraVerticalSpace = () => {
  const screen = getScreenDimensions();
  return screen.height - uiConfig.height;
};

export const getScaleFactor = () => {
  const screen = getScreenDimensions();
  return Math.min(screen.height / uiConfig.height, 1);
};

export const getScreenTop = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.height - uiConfig.height;
  const screenTop = -Math.ceil(extraSpace / 2);

  // console.log('>>> screenTop', extraSpace, screenTop);
  return screenTop;
};

export const getScreenBottom = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.height - uiConfig.height;
  const screenBottom = uiConfig.height + Math.ceil(extraSpace / 2);

  // console.log('>>> screenBottom', extraSpace, screenBottom);
  return screenBottom;
};

export const getScreenLeft = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.width - uiConfig.width;
  const screenLeft = -Math.ceil(extraSpace / 2);

  // console.log('>>> screenLeft', extraSpace, screenLeft);
  return screenLeft;
};

export const getScreenRight = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.width - uiConfig.width;
  const screenRight = uiConfig.width + Math.ceil(extraSpace / 2);

  // console.log('>>> screenRight', extraSpace, screenRight);
  return screenRight;
};

export const animDuration: number = 200;

/**
 * Whether the launch splash injected from index.html is still covering the
 * game. While it is, no scene or screen transition can be seen, so callers
 * can enter instantly instead of animating behind it.
 */
export const isSplashScreenVisible = () =>
  typeof document !== 'undefined' && !!document.getElementById('splashScreen');

// waiting functions
export const waitForIt = (
  cb: () => void,
  duration = animDuration,
  it: object = {},
) => {
  animate(it)
    .clear()
    .wait(duration)
    .then(() => {
      animate(it).clear();
      cb();
    });
};

export const waitForItPromise = async (duration = animDuration) =>
  new Promise<void>((resolve) => waitForIt(resolve, duration));

export const createWaiter = () => {
  const handle = {};
  return {
    wait: (cb: () => void, duration = animDuration) =>
      waitForIt(cb, duration, handle),
    clear: () => animate(handle).clear(),
  };
};

export const createWaiterInView = (view: View) => {
  const waiter = createWaiter();

  view.on('ViewRemoved', () => void waiter.clear());

  return waiter;
};

type CachedItem<T> = { data: T; updatedAt: number };
const cache: Record<string, CachedItem<any>> = {};
const fetchingPromise: Record<string, Promise<any>> = {};
const failedFetch: Record<string, { promise: Promise<any>; failedAt: number }> =
  {};
// Callers poll `get` (some every second), so a failure is reused for this long rather than
// retrying a failing backend on every call.
const failedFetchCooldown = duration({ seconds: 30 });
const forceUpdatePromise: Record<
  string,
  { promise: Promise<void>; resolve: () => void }
> = {};

type Cache<T> = {
  get: () => Promise<T>;
  invalidate: () => Promise<void>;
  getCached: () => T | undefined;
  setCached: (
    setter: (data: CachedItem<T> | undefined) => CachedItem<T> | undefined,
  ) => void;
  waitForRefresh: () => Promise<T>;
};

export function useCache<T>(
  getter: () => Promise<T>,
  hash: string,
  maxAge = duration({ minutes: 5 }),
): Cache<T> {
  const resetForceUpdate = () => {
    let resolve: VoidFunction = () => void 0;
    const promise = new Promise<void>((res) => (resolve = res));
    forceUpdatePromise[hash] = { promise, resolve };
  };
  const forceUpdate = () => {
    forceUpdatePromise[hash]?.resolve();
    resetForceUpdate();
  };

  if (!forceUpdatePromise[hash]) {
    resetForceUpdate();
  }

  const api: Cache<T> = {
    get: () => getWithCache(getter, hash, maxAge),
    invalidate: () => invalidateCache(hash),
    getCached: () => cache[hash]?.data,
    setCached: (setter) => {
      const newData = setter(cache[hash]);
      if (newData) {
        cache[hash] = newData;
      } else {
        delete cache[hash];
      }
      forceUpdate();
    },
    waitForRefresh: async () => {
      const timeout = async () => {
        // Ongoing fetch promise, wait for it to resolve.
        if (fetchingPromise[hash] != null) {
          return fetchingPromise[hash];
        }

        // Trigger refresh when cache is stale and no failure is cooling down.
        const timeSinceLastUpdate =
          StateObserver.now() - (cache[hash]?.updatedAt ?? 0);
        const timeTillRefresh = Math.max(
          maxAge - timeSinceLastUpdate,
          getFailedFetchCooldownLeft(hash),
        );
        if (timeTillRefresh > 0) {
          await waitForItPromise(timeTillRefresh);
        }
      };

      // Wait for fetch or force update.
      await Promise.race([timeout(), forceUpdatePromise[hash].promise]);

      // Return updated data.
      return api.get();
    },
  };

  return api;
}

async function getWithCache<T>(
  getter: () => Promise<T>,
  hash: string,
  maxAge = duration({ minutes: 5 }),
) {
  // Cache hit.
  if (cache[hash] && StateObserver.now() - cache[hash].updatedAt < maxAge) {
    return cache[hash].data;
  }

  // Cache miss.

  // If fetching promise exists, wait for it to resolve.
  if (fetchingPromise[hash] != null) {
    // Return existing fetching promise.
    return fetchingPromise[hash];
  }

  if (getFailedFetchCooldownLeft(hash) > 0) {
    return failedFetch[hash].promise;
  }

  // Fetch data.
  const promise = getter().then((data) => {
    // Don't cache nulls.
    if (!data) return data;

    // Cache data.
    cache[hash] = { data, updatedAt: StateObserver.now() };

    // Return data.
    return data;
  });
  fetchingPromise[hash] = promise;
  delete failedFetch[hash];

  promise.then(
    () => delete fetchingPromise[hash],
    () => {
      delete fetchingPromise[hash];
      failedFetch[hash] = { promise, failedAt: StateObserver.now() };
    },
  );

  // Return fetching promise.
  return promise;
}

function getFailedFetchCooldownLeft(hash: string) {
  const failure = failedFetch[hash];
  if (!failure) return 0;
  return Math.max(
    0,
    failedFetchCooldown - (StateObserver.now() - failure.failedAt),
  );
}

async function invalidateCache(hash: string) {
  if (fetchingPromise[hash] != null) {
    // The fetch's own caller handles its failure; invalidation only waits for it to settle.
    await fetchingPromise[hash].catch(() => undefined);
  }
  // An explicit invalidation retries a failed fetch without waiting for its cooldown.
  delete failedFetch[hash];
  // Keep the cache entry, but mark it as stale.
  if (cache[hash]) cache[hash].updatedAt = 0;
  delete forceUpdatePromise[hash];
}

type SpinnerConfig = ImageViewOpts & {
  superview: View;
  color?: string;
};

export async function withLoading<T>(
  fn: () => Promise<T>,
  config?: Partial<FeatureAnalytics & SpinnerConfig>,
): Promise<T> {
  const queryablePromise = createQueryablePromiseFromPromise(fn());

  // Wait for 100ms before showing loading.
  await Promise.race([delay(100), queryablePromise]);

  // Promise is ready, no need to show loading.
  if (queryablePromise.isReady()) return queryablePromise;

  // Store the loader result.
  let result: T;

  // Show global loader if no config is provided.
  if (!config?.superview) {
    result = await showGlobalLoader(queryablePromise);
  } else {
    // Show local spiner.
    result = await showLocalLoader(queryablePromise, config as SpinnerConfig);
  }

  // Return the result.
  return result;
}

async function showGlobalLoader<T>(queryablePromise: QueryablePromise<T>) {
  // Show loading if it's taking longer.
  StateObserver.dispatch(showLoading());
  try {
    return await queryablePromise;
  } finally {
    StateObserver.dispatch(hideLoading());
  }
}

let spinnerPool: QuickViewPool<ImageView> | null = null;
const getSpinnerPool = () => {
  if (!spinnerPool) {
    spinnerPool = new QuickViewPool(
      () =>
        new ImageView({
          image: 'assets/ui/shared/icons/loading_spinner.png',
        }),
    );
  }

  return spinnerPool;
};
async function showLocalLoader<T>(
  queryablePromise: QueryablePromise<T>,
  config: SpinnerConfig,
) {
  const { superview } = config;
  const size = Math.min(
    superview.style.width / 2,
    superview.style.height / 2,
    100,
  );
  const spinner = getSpinnerPool().obtainView();
  spinner.updateOpts({
    width: size,
    height: size,
    x: superview.style.width / 2,
    y: superview.style.height / 2,
    centerOnOrigin: true,
    ...config,
  });

  if (config.color) {
    spinner.setFilter(new MultiplyFilter(config.color));
  } else {
    spinner.removeFilter();
  }

  const spinnerAnim = animate(spinner);
  const loopSpinner = () => {
    spinnerAnim
      .clear()
      .then({ r: 0 }, 0)
      .then({ r: 2 * Math.PI }, 1000, animate.linear)
      .then(loopSpinner);
  };
  loopSpinner();

  try {
    return await queryablePromise;
  } finally {
    spinnerAnim.clear();
    spinner.removeFromSuperview();
  }
}

export const waitForAssets = async (
  assets: AssetGroup,
  args: FeatureAnalytics,
) => {
  if (assets.isLoaded()) return;

  await withLoading(() => assets.load(), args);
};

export const waitForMultipleAssets = async (
  assets: AssetGroup[],
  args: FeatureAnalytics,
) => {
  const wait = assets
    .filter((group) => !group.isLoaded())
    .map((group) => group.load());

  await withLoading(() => Promise.all(wait), args);
};

export const parseAmount = beautifyNumber.parseAmount;

export const toFixedTrunc = beautifyNumber.toFixedTrunc;

export const toShort = beautifyNumber.toShort;

export const toAmountShort = (amount: number) =>
  beautifyNumber.toAmountShort(amount, StateObserver.getState().ui.locale);

export const serverSyncedLocalTime = () =>
  StateObserver.now() - new Date().getTimezoneOffset() * 60 * 1000;

export const getApplication = () => {
  const app = engine.getView() as Application | null;
  if (!app) {
    throw new Error('Application is not initialized');
  }
  return app;
};

export const getRootView = () => getApplication().getRootView();

export const getScreen = (screenId: ScreenId) =>
  getApplication().screenManager.screens[screenId];

export const getSceneManager = () => {
  const petSceneManager =
    getApplication().screenManager.screens.pet?.sceneManager;
  if (!petSceneManager) {
    throw new Error('No pet scene, cannot get scene manager');
  }
  return petSceneManager;
};

export const getHeader = () => getApplication().screenManager.header;

export const getFooter = () => getApplication().screenManager.footer;

// Vertical position for the side buttons, anchored below the header's energy HUD
export function getSideButtonsY(relativeTo?: View): number {
  const { energyLayout, energyIcon, energyRegenerationFrame } = uiConfig.hud;
  let { y } = getHeader()!.getEnergyPos(relativeTo);
  y += energyLayout.height;
  y += energyRegenerationFrame.height;
  y -= energyIcon.height / 2;
  return y;
}

// Wait and block taps for a specified amount of time
export async function blockUI(millis: number) {
  const blockEvents = new View({
    superview: getRootView(),
    infinite: true,
    zIndex: 100000,
  });

  await waitForItPromise(millis);

  blockEvents.removeFromSuperview();
}

export const isPlatformGuest = (
  platform: Platform,
): platform is PlatformLineGuest => platform.platformID === 'line-guest';

const products = [
  {
    id: 'buruburu_gems_100',
    title: '100 Gems',
    description: '100 Gems',
    imageURI: 'buruburu_gems_100',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_gems_350',
    title: '350 Gems',
    description: '350 Gems',
    imageURI: 'buruburu_gems_350',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_gems_1000',
    title: '1000 Gems',
    description: '1000 Gems',
    imageURI: 'buruburu_gems_1000',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_gems_3200',
    title: '3200 Gems',
    description: '3200 Gems',
    imageURI: 'buruburu_gems_3200',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_gems_6000',
    title: '6000 Gems',
    description: '6000 Gems',
    imageURI: 'buruburu_gems_6000',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_gems_12000',
    title: '12000 Gems',
    description: '12000 Gems',
    imageURI: 'buruburu_gems_12000',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_gems_18000',
    title: '18000 Gems',
    description: '18000 Gems',
    imageURI: 'buruburu_gems_18000',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_piggy_bank_200',
    title: 'Piggy Bank',
    description: 'Piggy Bank (¥200)',
    imageURI: 'buruburu_piggy_bank_200',
    currencyCode: 'JPY',
  },
  {
    id: 'buruburu_piggy_bank_500',
    title: 'Piggy Bank',
    description: 'Piggy Bank (¥500)',
    imageURI: 'buruburu_piggy_bank_500',
    currencyCode: 'JPY',
  },
] as const;

export const regularOpenWebCatalog: Product[] = products.map((product) => ({
  ...product,
  price: `${webIAPPrices[product.id]}`,
}));

export const regularNativeCatalog: Product[] = products.map((product) => ({
  ...product,
  price: `${prices[product.id]}`,
}));

export const getWebIAPCatalog = (): Product[] => {
  const catalog: Product[] = [];

  // Append regular catalog
  catalog.push(...regularOpenWebCatalog);

  // Append shop bundles catalog
  catalog.push(...shopBundlesGetWebIAPCatalog());

  // Append bundle sale catalog
  catalog.push(...match2BundleSaleGetWebIAPCatalog());

  // Filter out repeated items and return the catalog
  return catalog.filter((item, pos) => {
    return catalog.indexOf(item) === pos;
  });
};

export function getUrlSearchParamValue(paramName: string) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

export function getSupportMail(): string {
  return supportEmail.bd;
}

export function canOpenExternalWindow() {
  // Desktop, native, liff can open window
  return true;
}

export function getCreativeSuffix() {
  return StateObserver.getState().user.settings.locale === 'ja' ? 'jp' : 'en';
}

/**
 * Preloads an array of asset urls that can be `null` or `undefined`.
 * Useful when preloading user icons that can be nulls.
 */
export async function preloadImages(
  assetUrls: (string | null | undefined)[],
): Promise<void> {
  await Promise.all(
    assetUrls.filter((x) => !!x).map((x) => createImageDescriptor(x!)),
  );
}

/**
 * Split array by chunks
 */
export function chunk<T>(data: T[], size: number): T[][] {
  const results = [];

  for (let i = 0; i < data.length; i += size) {
    const chunk = data.slice(i, i + size);
    results.push(chunk);
  }

  return results;
}

export async function delay(ms: number): Promise<void> {
  if (ms === 0) return;
  return new Promise((res) => setTimeout(res, ms));
}

export async function retry<T>(
  action: () => Promise<T>,
  options?: {
    /** @defaultValue 2 */
    retryCount?: number;

    retryOnlyIf?: (error: Error) => boolean;

    /** @defaultValue 500 */
    linearBackoff?: number;
  },
): Promise<T> {
  const retryOnlyIf = options?.retryOnlyIf ?? (() => true);
  const retryCount = options?.retryCount ?? 2;

  const backoff = options?.linearBackoff ?? 500;
  let currentRetry = 0;

  do {
    try {
      return await action();
    } catch (error: any) {
      const shouldRetry = currentRetry < retryCount && retryOnlyIf(error);

      if (!shouldRetry) {
        throw error;
      } else {
        currentRetry++;
        await delay((currentRetry - 1) * backoff);
      }
    }
  } while (true); // eslint-disable-line no-constant-condition
}

/**
 * Return currency icon
 */
export function getCurrencyIcon(
  currency:
    | 'gems'
    | 'bPoints'
    | 'energy'
    | 'bpoints'
    | 'ticket_silver'
    | 'ticket_bronze',
) {
  switch (currency) {
    case 'bpoints':
    case 'bPoints':
      return {
        image: 'assets/ui/hud/icons/hud_coin.png',
        width: 45,
        height: 46,
      };
    case 'gems':
      return {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 50,
        height: 40,
      };
    case 'energy':
      return {
        image: 'assets/ui/hud/icons/hud_energy.png',
        width: 39,
        height: 46,
      };
    case 'ticket_bronze':
      return {
        image: 'assets/ui/gacha/icons/ticket_bronze.png',
        width: 40,
        height: 53,
      };
    case 'ticket_silver':
      return {
        image: 'assets/ui/gacha/icons/ticket_silver.png',
        width: 40,
        height: 53,
      };
    default:
      assertNever(currency);
  }
}

export function applyRelativeOffset(
  position: ViewPosition,
  curView: View,
  relView: View,
) {
  if (!curView.getSuperview()) return;
  const offset = getRelativeOffset(curView, relView);
  position.x += offset.x;
  position.y += offset.y;
}

type AppVisibilityListener = (state: DocumentVisibilityState) => void;

export function addAppVisibilityListener(listener: AppVisibilityListener) {
  document.addEventListener('visibilitychange', () => {
    listener(document.visibilityState);
  });
}

/**
 * Wait for the visibility to return.
 *
 * @returns Promise that resolves when the app is in the foreground.
 */
export function waitForVisibilityChange() {
  return new Promise<void>((resolve) => {
    const listener = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', listener);
        resolve();
      }
    };
    document.addEventListener('visibilitychange', listener);
  });
}

// Wait for the app to be in the foreground before executing the promise.
export async function foregroundResolve<T>(
  promise: () => Promise<T>,
): Promise<T> {
  await StateObserver.waitForForeground();

  return promise();
}

// Create a getter that waits for the app to be in the foreground before executing.
export function foregroundAsyncGetter<T>(
  getter: () => Promise<T>,
): () => Promise<T> {
  return async () => {
    await StateObserver.waitForForeground();

    return getter();
  };
}

/**
 * Run a promise in a loop while the view is in the hierarchy.
 * @param view View to check if it's in the view hierarchy.
 * @param promise Promise to run in a loop.
 * @param initialState Initial state of the loop.
 * @returns
 */
export function whileInHierarchy(
  view: View,
  promise: (stop: VoidFunction) => Promise<void>,
  initialState = true,
) {
  let running = false;

  const start = () => {
    if (running) return;
    running = true;
    void chain();
  };
  const stop = () => {
    running = false;
  };

  const chain = async () => {
    if (!running) return;
    await promise(stop);
    void chain();
  };

  if (initialState) {
    start();
  }

  view.on('ViewAdded', () => {
    start();
  });

  view.on('ViewRemoved', () => {
    stop();
  });
}

/**
 * Always returns false — was gated on platformID === 'line' which no longer exists.
 * TODO: Remove this function and its callers.
 */
export function isOpenWeb() {
  return false;
}

/**
 * Determines whether the provided error is a `DOMException` with the name `'QuotaExceededError'`.
 *
 * This is typically used to detect when browser storage (such as localStorage or IndexedDB)
 * has exceeded its quota and cannot store additional data.
 *
 * @param error - The error object to check.
 * @returns `true` if the error is a `DOMException` with the name `'QuotaExceededError'`, otherwise `false`.
 */
export function isQuotaExceededError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === 'QuotaExceededError';
}
